import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import {
  Grid,
  Card,
  Text,
  Group,
  Button,
  Stack,
  Loader,
  Alert,
  Box,
  Title,
  ThemeIcon,
  Table,
  Avatar,
  Badge,
} from "@mantine/core";
import {
  IconUsers,
  IconUserCheck,
  IconClock,
  IconRefresh,
  IconAlertCircle,
  IconMail,
  IconMessageCircle,
  IconChecklist,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import EmailContentModal from "./EmailContentModal";

// Helper functions
const formatLastSeen = lastActiveTime => {
  if (!lastActiveTime) return "Never";

  const lastActiveDate = new Date(lastActiveTime);
  const today = new Date();

  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const lastActiveStart = new Date(
    lastActiveDate.getFullYear(),
    lastActiveDate.getMonth(),
    lastActiveDate.getDate()
  );

  const diffTime = todayStart - lastActiveStart;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "1 day ago";
  } else {
    return `${diffDays} days ago`;
  }
};

const getActivityLevel = rep => {
  if (!rep.verified) return "Inactive";
  if (!rep.last_active_time) return "Inactive";

  const lastActiveDate = new Date(rep.last_active_time);
  const today = new Date();
  const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  return lastActiveDate >= oneWeekAgo ? "Active" : "Inactive";
};

// Custom Users Table component
const AdminUsersTable = ({
  reps,
  onResendInvite,
  onPitchReview,
  onEmailContent,
  onTasks,
  resendingInvite,
}) => {
  if (!reps || reps.length === 0) {
    return (
      <Box ta="center" py="xl">
        <Text c="dimmed">No users found.</Text>
      </Box>
    );
  }

  const flattenReps = (reps, level = 0) => {
    let flatReps = [];
    reps.forEach(rep => {
      flatReps.push({ ...rep, level });
      if (rep.reps && rep.reps.length > 0) {
        flatReps = flatReps.concat(flattenReps(rep.reps, level + 1));
      }
    });
    return flatReps;
  };

  const flatReps = flattenReps(reps);

  const rows = flatReps.map(rep => (
    <Table.Tr key={rep.rep_id}>
      <Table.Td>
        <Group gap="sm">
          <Box style={{ paddingLeft: rep.level * 20 }}>
            <Avatar
              src={rep.rep_type?.image}
              size="sm"
              radius="xl"
              alt={rep.rep_type?.name || "Rep Type"}
            >
              {rep.role_name?.charAt(0)?.toUpperCase() || "R"}
            </Avatar>
          </Box>
          <Box>
            <Text fw={500}>
              {rep.first_name} {rep.last_name}
            </Text>
            <Text size="sm" c="dimmed">
              {rep.email || "N/A"}
            </Text>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color="indigo" variant="light" size="sm">
          {rep.rep_id}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge
          color={rep.verified ? "green" : "orange"}
          variant="light"
          size="sm"
        >
          {rep.verified ? "VERIFIED" : "PENDING"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge
          color={getActivityLevel(rep) === "Active" ? "blue" : "gray"}
          variant="light"
          size="sm"
        >
          {getActivityLevel(rep).toUpperCase()}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatLastSeen(rep.last_active_time)}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {!rep.verified && onResendInvite && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconMail size={14} />}
              onClick={() => onResendInvite(rep.rep_id)}
              disabled={resendingInvite === rep.rep_id}
              loading={resendingInvite === rep.rep_id}
            >
              {resendingInvite === rep.rep_id ? "Sending..." : "Resend Invite"}
            </Button>
          )}
          {rep.pitch_review_channel &&
            rep.pitch_review_channel.id &&
            onPitchReview && (
              <Button
                variant="filled"
                size="xs"
                leftSection={<IconMessageCircle size={14} />}
                onClick={() => onPitchReview(rep.pitch_review_channel.id)}
              >
                Pitch Review
              </Button>
            )}
          {onTasks && (
            <Button
              variant="light"
              color="violet"
              size="xs"
              leftSection={<IconChecklist size={14} />}
              onClick={() => onTasks(rep)}
            >
              Tasks
            </Button>
          )}
          {onEmailContent && (
            <Button
              variant="outline"
              size="xs"
              leftSection={<IconMail size={14} />}
              onClick={() =>
                onEmailContent(rep.rep_id, `${rep.first_name} ${rep.last_name}`)
              }
            >
              Email Content
            </Button>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>User</Table.Th>
          <Table.Th>Rep ID</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Activity</Table.Th>
          <Table.Th>Last Seen</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
};

function AdminCompanyUsersPage({ companyCode }) {
  const { code: urlCode } = useParams();
  const code = companyCode || urlCode;
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalReps, setTotalReps] = useState(0);
  const [totalActiveReps, setTotalActiveReps] = useState(0);
  const [totalPendingReps, setTotalPendingReps] = useState(0);
  const [client, setClient] = useState(null);

  // Check if user has access (REPPI_ADMIN only)
  useEffect(() => {
    if (!user || user.role !== "REPPI_ADMIN") {
      navigate("/dashboard");
      return;
    }

    if (code) {
      // Keep the admin tools that aren't opened from a company page pointed at
      // the company being worked on.
      setAdminCompanyCode(code);
      fetchCompanyUsers();
      fetchClientDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, user]);

  const [resendingInvite, setResendingInvite] = useState(null);

  // Email Content Modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedRepId, setSelectedRepId] = useState(null);
  const [selectedRepName, setSelectedRepName] = useState("");

  // Fetch client details for header context
  const fetchClientDetails = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/client/${code}`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setClient(result.data || result);
      } else {
        showError("Failed to fetch client details");
      }
    } catch (err) {
      handleGenericError(err, "Error trying to fetch client details");
    }
  };

  // Fetch company users from admin API endpoint
  const fetchCompanyUsers = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getAdminApiEndpoint(`/v1/admin/company/${code}/users`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const summary_data = result.data.summary_metrics || {};
        const reps = result.data?.reps ?? [];
        setReps(reps);
        setTotalReps(summary_data.total_users ?? 0);
        setTotalActiveReps(summary_data.total_active_users ?? 0);
        setTotalPendingReps(summary_data.total_pending_users ?? 0);
        setError(null);
      } else {
        setError("Failed to fetch company users");
      }
    } catch (err) {
      setError("Failed to fetch company users");
    } finally {
      setLoading(false);
    }
  };

  // Resend invite to rep - Note: This might need to be updated with a company-specific endpoint
  const handleResendInvite = async repId => {
    try {
      setResendingInvite(repId);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Using the same endpoint as rep management for now - this might need to be changed
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/invite/${repId}`),
        {
          method: "POST",
          headers: adminHeaders(jwt, code),
        }
      );

      if (response.ok) {
        showSuccess("Invite resent successfully!");
      } else {
        handleApiError(
          new Error(`HTTP ${response.status} - ${response.statusText}`),
          "Failed to resend invite"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to resend invite");
    } finally {
      setResendingInvite(null);
    }
  };

  // Navigate to pitch review page
  const handlePitchReview = pitchReviewChannelId => {
    navigate(`/pitch-review/${pitchReviewChannelId}`);
  };

  // Handle email content modal
  const handleEmailContent = (repId, repName) => {
    setSelectedRepId(repId);
    setSelectedRepName(repName);
    setIsEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setSelectedRepId(null);
    setSelectedRepName("");
  };

  if (!user || user.role !== "REPPI_ADMIN") {
    return (
      <PageContent
        title="Access Denied"
        subtitle="You do not have permission to access this page"
      >
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Denied"
          color="red"
        >
          Only REPPI admins can access this page.
        </Alert>
      </PageContent>
    );
  }

  return (
    <PageContent
      title={`Company Users - ${client?.name || "Client"}`}
      subtitle="Manage company users and view performance metrics"
    >
      <Stack gap="lg">
        {/* Metrics Cards - First Row */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
            <Card padding="lg" radius="md" withBorder h="100%">
              <Group>
                <ThemeIcon size="lg" variant="light" color="blue">
                  <IconUsers size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text size="sm" color="dimmed" fw={500}>
                    Total Users
                  </Text>
                  <Text size="xl" fw={700}>
                    {totalReps} Users
                  </Text>
                </Box>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
            <Card padding="lg" radius="md" withBorder h="100%">
              <Group>
                <ThemeIcon size="lg" variant="light" color="green">
                  <IconUserCheck size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text size="sm" color="dimmed" fw={500}>
                    Active Users
                  </Text>
                  <Text size="xl" fw={700}>
                    {totalActiveReps} Active Users
                  </Text>
                </Box>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
            <Card padding="lg" radius="md" withBorder h="100%">
              <Group>
                <ThemeIcon size="lg" variant="light" color="orange">
                  <IconClock size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text size="sm" color="dimmed" fw={500}>
                    Pending Users
                  </Text>
                  <Text size="xl" fw={700}>
                    {totalPendingReps} Pending
                  </Text>
                </Box>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Company Users Section */}
        <Card padding="lg" radius="md" withBorder>
          <Stack gap="lg">
            <Group justify="space-between" align="center">
              <Title order={3}>Company Users</Title>
              <Group>
                <Button
                  variant="outline"
                  leftSection={<IconRefresh size={16} />}
                  onClick={fetchCompanyUsers}
                >
                  Refresh
                </Button>
              </Group>
            </Group>

            {loading && (
              <Group justify="center" py="xl">
                <Loader size="md" />
                <Text>Loading users...</Text>
              </Group>
            )}

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
              >
                <Group justify="space-between">
                  <Text>{error}</Text>
                  <Button
                    variant="outline"
                    size="sm"
                    leftSection={<IconRefresh size={16} />}
                    onClick={fetchCompanyUsers}
                  >
                    Retry
                  </Button>
                </Group>
              </Alert>
            )}

            {!loading && !error && (
              <Box>
                {reps.length === 0 ? (
                  <Text ta="center" c="dimmed" py="xl">
                    No users found.
                  </Text>
                ) : (
                  <AdminUsersTable
                    reps={reps}
                    onResendInvite={handleResendInvite}
                    onPitchReview={handlePitchReview}
                    onEmailContent={handleEmailContent}
                    resendingInvite={resendingInvite}
                  />
                )}
              </Box>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Email Content Modal */}
      <EmailContentModal
        isOpen={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        repId={selectedRepId}
        repName={selectedRepName}
        companyCode={code}
      />
    </PageContent>
  );
}

export default AdminCompanyUsersPage;
